import type {
  AutomationProjectDto,
  AutomationScanDiffItem,
  AutomationScanPreviewDto,
} from '@tms/shared';
import {
  AlertTriangle,
  Check,
  Database,
  FileCode2,
  GitBranch,
  Layers,
  RefreshCw,
  Terminal,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Dialog } from '../../../components/ui';
import type { MessageKey } from '../../../i18n';
import { useT } from '../../../i18n';
import { useApplyScan, useScanRepo } from '../hooks';
import { useAutomationErrorText, useFailToast } from './shared';

type Section = 'suites' | 'classes' | 'data';
const SECTIONS: Array<{ key: Section; label: MessageKey; icon: typeof Layers }> = [
  { key: 'suites', label: 'automation.project.tab.suites', icon: Layers },
  { key: 'classes', label: 'automation.project.tab.classes', icon: FileCode2 },
  { key: 'data', label: 'automation.project.tab.data', icon: Database },
];
const SHOWN_ITEMS = 40;

const countsOf = (items: AutomationScanDiffItem[]) => ({
  added: items.filter((i) => i.status === 'new').length,
  updated: items.filter((i) => i.status === 'updated').length,
  removed: items.filter((i) => i.status === 'removed').length,
});

const ITEM_STYLES: Record<AutomationScanDiffItem['status'], string> = {
  new: 'bg-pass-soft text-pass',
  updated: 'bg-block-soft text-block',
  removed: 'bg-fail-soft text-fail',
  unchanged: '',
};

const LogBlock = ({ logs }: { logs: string[] }) => {
  const { t } = useT();
  if (!logs.length) return null;
  return (
    <div>
      <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        <Terminal className="size-3.5" aria-hidden="true" />
        {t('automation.project.scan.log')}
      </p>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-sunken p-3 font-mono text-[12px] leading-relaxed text-ink">
        {logs.join('\n')}
      </pre>
    </div>
  );
};

/**
 * Scanning a repository is two steps: the server clones it and shows what
 * would change in the suites, classes and test data; ticked parts are applied.
 */
export const ScanRepoDialog = ({
  project,
  onOpenChange,
}: {
  project: AutomationProjectDto;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const errorText = useAutomationErrorText();
  const failToast = useFailToast();
  const scan = useScanRepo(project.id);
  const apply = useApplyScan(project.id);
  const [result, setResult] = useState<AutomationScanPreviewDto | null>(null);
  const [pick, setPick] = useState<Record<Section, boolean>>({
    suites: true,
    classes: true,
    data: true,
  });

  const run = () => {
    setResult(null);
    scan.mutate(undefined, { onSuccess: setResult });
  };

  const doApply = () =>
    apply.mutate(pick, {
      onSuccess: (applied) => {
        toast.success(
          t('automation.project.scan.applied', {
            count: SECTIONS.filter((s) => pick[s.key]).length,
          }),
        );
        // @TmsLink in the code: say what it linked and which keys TMS does not know
        if (applied.caseLinks.linked > 0) {
          toast.info(t('automation.caseLinks.scanLinked', { count: applied.caseLinks.linked }));
        }
        if (applied.caseLinks.converted > 0) {
          toast.info(
            t('automation.caseLinks.scanConverted', { count: applied.caseLinks.converted }),
          );
        }
        if (applied.caseLinks.gone > 0) {
          toast.warning(t('automation.caseLinks.scanGone', { count: applied.caseLinks.gone }));
        }
        if (applied.caseLinks.unknown.length > 0) {
          toast.warning(
            t('automation.caseLinks.scanUnknown', { keys: applied.caseLinks.unknown.join(', ') }),
          );
        }
        onOpenChange(false);
      },
      onError: failToast,
    });

  const hasChanges =
    result?.ok &&
    SECTIONS.some((s) => {
      const c = countsOf(result[s.key]);
      return c.added + c.updated + c.removed > 0;
    });

  const footer = result?.ok ? (
    <>
      <span className="mr-auto text-[12px] text-ink-subtle">
        {hasChanges ? t('automation.project.scan.applyHint') : t('automation.project.scan.noDiff')}
      </span>
      <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
      <Button
        variant="primary"
        disabled={!Object.values(pick).some(Boolean)}
        loading={apply.isPending}
        onClick={doApply}
      >
        <Check className="size-3.5" />
        {t('automation.project.scan.apply')}
      </Button>
    </>
  ) : result || scan.error ? (
    <>
      <Button onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
      <Button variant="primary" loading={scan.isPending} onClick={run}>
        <RefreshCw className="size-3.5" />
        {t('common.retry')}
      </Button>
    </>
  ) : undefined;

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={t('automation.project.scan.title')}
      size="lg"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg bg-canvas p-3">
          <dl className="flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
            {(
              [
                [t('automation.project.scan.project'), project.name],
                [t('automation.project.field.gitlabProjectId'), project.gitlabProjectId],
                [t('automation.project.scan.type'), project.testType],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex flex-col gap-0.5">
                <dt className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</dt>
                <dd className="font-semibold text-ink">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-2 break-all font-mono text-[11.5px] text-ink-subtle">
            {project.gitlabPipelineUrl}
          </p>
        </div>

        {!result && !scan.error ? (
          <>
            <p className="text-[13px] text-ink-muted">{t('automation.project.scan.intro')}</p>
            <Button variant="primary" loading={scan.isPending} onClick={run}>
              {scan.isPending ? null : <RefreshCw className="size-4" />}
              {scan.isPending
                ? t('automation.project.scan.scanning')
                : t('automation.project.scan')}
            </Button>
            {scan.isPending ? (
              <p className="flex items-center gap-2 text-[12px] text-ink-subtle">
                <GitBranch className="size-3.5" aria-hidden="true" />
                {t('automation.project.scan.steps')}
              </p>
            ) : null}
          </>
        ) : null}

        {scan.error ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg bg-fail-soft px-3 py-2.5 text-[13px] text-ink"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fail" aria-hidden="true" />
            {errorText(scan.error)}
          </p>
        ) : null}

        {result && !result.ok ? (
          <>
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-fail-soft px-3 py-2.5 text-[13px] text-ink"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fail" aria-hidden="true" />
              {t('automation.project.scan.failed')}
            </p>
            <LogBlock logs={result.logs} />
          </>
        ) : null}

        {result?.ok ? (
          <>
            <p className="flex items-center gap-2 rounded-lg bg-pass-soft px-3 py-2 text-[13px] text-ink">
              <Check className="size-4 text-pass" aria-hidden="true" />
              {t('automation.project.scan.done', { source: result.source ?? '—' })}
            </p>
            {result.repoPath ? (
              <p className="break-all font-mono text-[11.5px] text-ink-subtle">{result.repoPath}</p>
            ) : null}
            <LogBlock logs={result.logs} />

            <div className="flex flex-col gap-3">
              {SECTIONS.map(({ key, label, icon: Icon }) => {
                const items = result[key];
                const c = countsOf(items);
                const changed = items.filter((i) => i.status !== 'unchanged');
                return (
                  <div key={key} className="overflow-hidden rounded-lg border border-border">
                    <label className="flex cursor-pointer flex-wrap items-center justify-between gap-2 bg-canvas px-3 py-2.5">
                      <span className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={pick[key]}
                          onChange={(event) => setPick({ ...pick, [key]: event.target.checked })}
                          className="size-4 rounded border-border"
                        />
                        <Icon className="size-4 text-accent-fg" aria-hidden="true" />
                        <span className="text-[13px] font-semibold text-ink">{t(label)}</span>
                        <span className="text-[12px] text-ink-subtle">
                          {t('automation.project.scan.found', { count: items.length - c.removed })}
                        </span>
                      </span>
                      <span className="flex flex-wrap gap-1.5">
                        {c.added > 0 ? (
                          <Badge className={ITEM_STYLES.new}>
                            {t('automation.project.scan.new', { count: c.added })}
                          </Badge>
                        ) : null}
                        {c.updated > 0 ? (
                          <Badge className={ITEM_STYLES.updated}>
                            {t('automation.project.scan.updated', { count: c.updated })}
                          </Badge>
                        ) : null}
                        {c.removed > 0 ? (
                          <Badge className={ITEM_STYLES.removed}>
                            {t('automation.project.scan.removed', { count: c.removed })}
                          </Badge>
                        ) : null}
                        {c.added + c.updated + c.removed === 0 ? (
                          <Badge>{t('automation.project.scan.unchanged')}</Badge>
                        ) : null}
                      </span>
                    </label>
                    {changed.length > 0 ? (
                      <ul className="max-h-44 overflow-auto">
                        {changed.slice(0, SHOWN_ITEMS).map((item) => (
                          <li
                            key={item.name}
                            className="flex items-center justify-between gap-3 border-t border-border px-3 py-1.5 text-[12.5px]"
                          >
                            <span className="min-w-0 break-all font-mono">{item.name}</span>
                            <span className="flex shrink-0 items-center gap-2">
                              {item.detail ? (
                                <span className="text-ink-subtle">{item.detail}</span>
                              ) : null}
                              <Badge className={ITEM_STYLES[item.status]}>
                                {item.status === 'new'
                                  ? t('automation.project.scan.itemNew')
                                  : item.status === 'removed'
                                    ? t('automation.project.scan.itemRemoved')
                                    : t('automation.project.scan.itemUpdated')}
                              </Badge>
                            </span>
                          </li>
                        ))}
                        {changed.length > SHOWN_ITEMS ? (
                          <li className="border-t border-border px-3 py-1.5 text-[12px] text-ink-subtle">
                            {t('automation.project.scan.more', {
                              count: changed.length - SHOWN_ITEMS,
                            })}
                          </li>
                        ) : null}
                      </ul>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
};
